import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Gift, Mail, RefreshCw, Sparkles } from 'lucide-react';
import { generateGiftCertificatePDF, generateCertificateCode, formatCertDate } from '../utils/giftCertificatePDF';

const SESSION_OPTIONS = [
  { key: '30', label: '30-Minute', value: 55 },
  { key: '60', label: '60-Minute', value: 110 },
  { key: '90', label: '90-Minute', value: 160 },
];

const GiftCertificatePreviewPage = () => {
  const [searchParams] = useSearchParams();

  const initialSession = SESSION_OPTIONS.find((o) => o.key === searchParams.get('t')) || SESSION_OPTIONS[1];

  const [recipient, setRecipient] = useState(searchParams.get('r') || '');
  const [sessionKey, setSessionKey] = useState(initialSession.key);
  const [message, setMessage] = useState(searchParams.get('m') || '');
  const [occasion, setOccasion] = useState(searchParams.get('o') || '');
  const [buyerName, setBuyerName] = useState(searchParams.get('b') || '');
  const [code, setCode] = useState(searchParams.get('code') || generateCertificateCode());

  const session = useMemo(
    () => SESSION_OPTIONS.find((o) => o.key === sessionKey) || SESSION_OPTIONS[1],
    [sessionKey]
  );

  const issuedDate = useMemo(() => new Date(), []);
  const validThroughDate = useMemo(() => {
    const d = new Date(issuedDate);
    d.setFullYear(d.getFullYear() + 1);
    return d;
  }, [issuedDate]);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Gift Certificate Preview | Ayumi Suzuki';

    // Inject noindex meta for this internal-tool page only
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);

    return () => {
      document.title = prev;
      document.head.removeChild(meta);
    };
  }, []);

  const handleDownload = () => {
    generateGiftCertificatePDF({
      recipientName: recipient.trim() || 'Valued Recipient',
      sessionLength: session.label,
      value: session.value,
      message: message.trim(),
      occasion: occasion.trim(),
      code,
      issuedOn: formatCertDate(issuedDate),
      validThrough: formatCertDate(validThroughDate),
      buyerName: buyerName.trim(),
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50" data-testid="gift-preview-page">
      {/* meta: private tool — noindex */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Gift Certificate Preview — Ayumi Suzuki',
            robots: 'noindex, nofollow',
          }),
        }}
      />

      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link
            to="/gift-certificates"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors duration-300 group"
            data-testid="back-to-gifts-link"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Back to Gift Certificates</span>
          </Link>
          <span className="text-xs uppercase tracking-widest text-teal-600 font-semibold">Preview & Download</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-teal-700 mb-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">For Ayumi · Internal tool</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif text-gray-800 mb-3" data-testid="preview-title">
            Gift Certificate Preview
          </h1>
          <p className="text-gray-600 max-w-2xl">
            Review the details from the buyer's request, tweak anything if needed, and download the branded PDF to attach to your reply email.
          </p>
        </div>

        {/* Payment-first reminder */}
        <div className="mb-8 bg-amber-50 border-l-4 border-amber-400 rounded-r-2xl p-5" data-testid="payment-reminder">
          <p className="text-sm text-amber-900 leading-relaxed">
            <strong>Payment first, PDF second.</strong> Only download and send this certificate <em>after</em> the buyer's payment has cleared (Venmo / Zelle / in-person card). Record the certificate code in your paid-certificates list before emailing the PDF.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Certificate visual preview */}
          <div className="lg:col-span-3" data-testid="cert-visual">
            <div
              className="aspect-[11/8.5] bg-white rounded-2xl shadow-2xl relative overflow-hidden"
              style={{ border: '3px solid rgb(15, 118, 110)' }}
            >
              <div className="absolute inset-4 rounded-xl" style={{ border: '1px solid rgb(204, 251, 241)' }}></div>

              {/* corner dots */}
              {[
                'top-4 left-4', 'top-4 right-4', 'bottom-4 left-4', 'bottom-4 right-4',
              ].map((pos, i) => (
                <div key={i} className={`absolute ${pos} w-1.5 h-1.5 rounded-full`} style={{ background: 'rgb(251, 207, 232)' }}></div>
              ))}

              <div className="relative h-full flex flex-col items-center justify-between p-8 md:p-12 text-center">
                <div>
                  <p className="text-[10px] md:text-xs font-bold tracking-widest text-teal-700 mb-1">
                    AYUMI SUZUKI MASSAGE THERAPY
                  </p>
                  <p className="text-[9px] md:text-[10px] tracking-widest text-gray-500 mb-3">
                    CERTIFIED · MIDTOWN SACRAMENTO · 2528 I STREET
                  </p>
                  <div className="mx-auto w-16 h-px bg-teal-600 mb-6"></div>

                  <h2 className="font-serif italic text-4xl md:text-6xl text-gray-800 mb-4">Gift Certificate</h2>
                  <p className="text-[10px] md:text-xs tracking-widest text-gray-500 mb-3">PRESENTED TO</p>
                  <p className="font-serif text-2xl md:text-4xl text-teal-700 mb-1" data-testid="cert-recipient">
                    {recipient.trim() || 'Valued Recipient'}
                  </p>
                  <div className="mx-auto w-48 md:w-64 h-px bg-teal-100 mt-3 mb-6"></div>

                  <p className="text-[10px] md:text-xs tracking-widest text-gray-500 mb-2">IS ENTITLED TO ONE</p>
                  <p className="font-serif font-bold text-xl md:text-2xl text-gray-800 mb-1" data-testid="cert-session">
                    {session.label} Massage Session
                  </p>
                  <p className="text-[10px] md:text-xs tracking-widest text-teal-700 font-medium" data-testid="cert-value">
                    ${session.value} GIFT VALUE
                  </p>

                  {message.trim() && (
                    <p className="mt-6 max-w-md mx-auto italic text-gray-500 text-sm leading-snug" data-testid="cert-message">
                      "{message.trim()}"
                    </p>
                  )}
                </div>

                <div className="w-full grid grid-cols-2 gap-4 items-end text-left">
                  <div>
                    <div className="border-t border-gray-800 pt-1">
                      <p className="font-serif italic text-teal-700 text-lg -mt-6 text-center">Ayumi Suzuki</p>
                      <p className="text-[8px] md:text-[9px] tracking-widest text-gray-500 mt-1 text-center">
                        AYUMI SUZUKI · CAMTC
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[9px] md:text-[10px]">
                    <div>
                      <p className="font-bold tracking-widest text-teal-700">CERT NO.</p>
                      <p className="text-gray-800" data-testid="cert-code">{code}</p>
                      <p className="font-bold tracking-widest text-teal-700 mt-2">ISSUED</p>
                      <p className="text-gray-800">{formatCertDate(issuedDate)}</p>
                    </div>
                    <div>
                      <p className="font-bold tracking-widest text-teal-700">VALID THROUGH</p>
                      <p className="text-gray-800">{formatCertDate(validThroughDate)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-3 text-center italic">
              The downloaded PDF is landscape US Letter — prints cleanly on standard paper.
            </p>
          </div>

          {/* Edit panel */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-6 md:p-8 h-fit" data-testid="cert-edit-panel">
            <h3 className="text-lg font-serif text-gray-800 mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-teal-600" />
              Certificate details
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Recipient name</label>
                <input
                  type="text"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                  data-testid="edit-recipient-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Session length</label>
                <div className="grid grid-cols-3 gap-2" data-testid="edit-session-group">
                  {SESSION_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSessionKey(opt.key)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${
                        sessionKey === opt.key
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-teal-300'
                      }`}
                      data-testid={`edit-session-${opt.key}`}
                    >
                      {opt.key} min · ${opt.value}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Personal message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows="4"
                  placeholder="Optional note from the buyer to include on the certificate."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all resize-none"
                  data-testid="edit-message-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Occasion</label>
                <input
                  type="text"
                  value={occasion}
                  onChange={(e) => setOccasion(e.target.value)}
                  placeholder="Birthday, thank you, holiday…"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                  data-testid="edit-occasion-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Certificate number</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all font-mono text-sm"
                    data-testid="edit-code-input"
                  />
                  <button
                    type="button"
                    onClick={() => setCode(generateCertificateCode())}
                    className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
                    title="Generate new code"
                    data-testid="regen-code-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Auto-generated. Regenerate or edit as you like.</p>
              </div>

              <button
                type="button"
                onClick={handleDownload}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3.5 rounded-lg font-medium transition-all duration-300 hover:shadow-xl flex items-center justify-center gap-2"
                data-testid="download-pdf-btn"
              >
                <Download className="w-5 h-5" />
                Download PDF certificate
              </button>

              <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 text-sm text-teal-900">
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p>
                    Attach the downloaded PDF to your reply email. The recipient can print it, forward it, or bring it up on their phone at check-in.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCertificatePreviewPage;
